import React from 'react'
import styled from '@emotion/styled/macro'
import type { Color } from '~/types/Color'

const Section = styled.div`
  padding: 4px var(--gutter-medium) var(--gutter-small);
`

const ColorChipsGrid = styled.fieldset`
  display: flex;
  flex-wrap: wrap;
  gap: var(--gutter-small);
  border: 0;
  margin: 0;

  @media (max-width: 768px) {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: var(--gutter-small);
  }
  legend {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 1px, 1px);
    white-space: nowrap;
    border: 0;
  }
`

const ScrollContainer = styled.div`
  position: relative;

  @media (max-width: 768px) {
    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      width: 24px;
      pointer-events: none;
      z-index: 1;
    }

    &::before {
      left: 0;
      background: linear-gradient(
        to right,
        var(--white) 0%,
        rgba(255, 255, 255, 0) 100%
      );
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    &::after {
      right: 0;
      background: linear-gradient(
        to left,
        var(--white) 0%,
        rgba(255, 255, 255, 0) 100%
      );
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    &[data-fade-left='true']::before {
      opacity: 1;
    }

    &[data-fade-right='true']::after {
      opacity: 1;
    }
  }
`

const ColorChip = styled.button<{ selected: boolean }>`
  position: relative;
  width: 20px;
  height: 20px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ColorChipDot = styled.div<{ color: string; selected: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
`

const SelectedIndicator = styled.div`
  position: absolute;
  top: 50%;
  right: 50%;
  transform: translate(50%, -50%);
  width: 16px;
  height: 16px;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 1;

  &::after {
    content: '✓';
    color: var(--white);
    font-size: 12px;
    font-weight: bold;
  }
`

interface Props {
  availableColors: Color[]
  selectedColorIds: Set<string>
  onToggleColor: (colorId: string) => void
  onResetSelection: () => void
}

const ColorSelectionSectionComponent: React.FC<Props> = ({
  availableColors,
  selectedColorIds,
  onToggleColor,
  onResetSelection
}) => {
  const gridRef = React.useRef<HTMLFieldSetElement | null>(null)
  const containerRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    const el = gridRef.current
    const container = containerRef.current
    if (!el || !container) {
      return
    }

    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el
      const canScroll = scrollWidth > clientWidth + 1
      const atStart = scrollLeft <= 0
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 1

      container.dataset.fadeLeft = canScroll && !atStart ? 'true' : 'false'
      container.dataset.fadeRight = canScroll && !atEnd ? 'true' : 'false'
    }

    update()
    el.addEventListener('scroll', update, {
      passive: true
    } as unknown as AddEventListenerOptions)
    window.addEventListener('resize', update)
    return () => {
      el.removeEventListener('scroll', update as EventListener)
      window.removeEventListener('resize', update)
    }
  }, [])
  const selectedColors = availableColors.filter((color) =>
    selectedColorIds.has(color.id)
  )

  return (
    <Section>
      <ScrollContainer ref={containerRef}>
        <ColorChipsGrid ref={gridRef}>
          <legend>カラーパレット</legend>
          {availableColors.map((color) => {
            const isSelected = selectedColorIds.has(color.id)
            return (
              <ColorChip
                key={color.id}
                selected={isSelected}
                onClick={() => onToggleColor(color.id)}
                aria-label={`${color.name} ${isSelected ? '選択中' : '未選択'}`}
                aria-pressed={isSelected}
                title={color.name}
              >
                <ColorChipDot color={color.colorCode} selected={isSelected} />
                {isSelected && <SelectedIndicator />}
              </ColorChip>
            )
          })}
        </ColorChipsGrid>
      </ScrollContainer>
    </Section>
  )
}

export const ColorSelectionSection = React.memo(ColorSelectionSectionComponent)
